import type { RosterEntry } from '@league/shared/contract';
import { effectivePace, type PlayingPace } from '@league/shared/schemas';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ApiError, apiPatch } from '../../lib/api';
import { paceChoices, paceOverrideFor } from './roster-status';

/**
 * A player's pace of play in this league, as a dropdown in the "Pace of play" column — the director's
 * setting for the league, never the player's own profile choice; picking her own choice clears it
 * (owner, 2026-10-08). Shared by the roster, the waitlist and the league's subs.
 */
export function PaceSelect({ entry, leagueId, field = false }: { entry: RosterEntry; leagueId: string; field?: boolean }) {
  const queryClient = useQueryClient();
  const name = `${entry.player.firstName} ${entry.player.lastName}`;
  const current = effectivePace(entry.player.preferredPace ?? null, entry.enrollment.paceOverride ?? null);
  /* "Saved" for two seconds, then quiet. */
  const [saved, setSaved] = useState(false);
  const save = useMutation({
    mutationFn: (paceOverride: PlayingPace | null) => apiPatch(`/api/enrollments/${entry.enrollment.id}`, { paceOverride }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['roster', leagueId] }),
        queryClient.invalidateQueries({ queryKey: ['league-subs', leagueId] }),
      ]);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    },
  });
  return (
    <>
      <select
        aria-label={`Pace of play for ${name}`}
        value={current}
        disabled={save.isPending}
        onChange={(event) => save.mutate(paceOverrideFor(entry, event.target.value as PlayingPace))}
        className={[
          /* `field`: a full-size control, in the phone panel. Otherwise quiet, on a table row. */
          field
            ? 'h-11 w-full rounded-md border border-control-border bg-paper px-3 text-[16px] focus-visible:border-deep'
            : 'min-h-9 rounded-md border border-transparent bg-transparent px-2 text-[14px] hover:border-stroke focus-visible:border-deep',
          current === 'unknown' ? 'text-ink-3' : 'text-ink',
        ].join(' ')}
      >
        {paceChoices(entry).map((choice) => (
          <option key={choice.value} value={choice.value}>{choice.label}</option>
        ))}
      </select>
      {/* The word is always there, just hidden, so showing it moves nothing. */}
      <span aria-hidden="true" className={['ml-1 text-[13px] text-sage transition-opacity duration-300', saved ? 'opacity-100' : 'opacity-0'].join(' ')}>
        Saved
      </span>
      <span role="status" className="sr-only">{saved ? `Pace saved for ${name}` : ''}</span>
      {save.error ? (
        <span role="alert" className="block text-[13px] text-rust">
          {save.error instanceof ApiError ? save.error.message : 'Could not save that pace.'}
        </span>
      ) : null}
    </>
  );
}
